import React, { useState } from "react";
import { Box } from "@mui/material";
import VideoPlayer from "../../../../common/ui/VideoPlayer";

interface ConsultationVideoOverlayProps {
  /**
   * Per-use video URL (self-hosted MP4). `VideoPlayer` renders it in a native
   * `<video>`, so `onComplete` fires on the `ended` event.
   */
  videoUrl: string;
  /** Called when the video finishes playing. The consumer unmounts the
   *  overlay in response. (Native `<video>` only — see VideoPlayer.) */
  onComplete: () => void;
  /** Optional Skip handler. If omitted, the Skip button calls `onComplete`. */
  onSkip?: () => void;
  /** Label for the skip button. Defaults to "Skip". */
  skipLabel?: string;
  /** Whether to show the Skip button at all. Defaults to `true`. */
  showSkip?: boolean;
  /** Autoplay on mount (only safe when a recent user gesture allows it, e.g.
   *  the outro after clicking "Complete"). Defaults to `false`. */
  autoPlay?: boolean;
}

/**
 * Full-viewport video overlay used at the start (intro) and end (outro) of the
 * consultation. Fixed black backdrop with the shared `VideoPlayer` filling it
 * (branded play button → click to play) and a Skip button bottom-right.
 *
 * The player is our own component, not react-player / the YouTube JS API. The
 * consultation videos are self-hosted MP4s, so `VideoPlayer` renders a native
 * `<video>` and `onComplete` fires when the video ends.
 */
const ConsultationVideoOverlay: React.FC<ConsultationVideoOverlayProps> = ({
  videoUrl,
  onComplete,
  onSkip,
  skipLabel = "Skip",
  showSkip = true,
  autoPlay = false,
}) => {
  const handleSkip = onSkip ?? onComplete;
  // Only offer Skip once the video is actually playing — there's nothing to
  // skip while the play button is still showing.
  const [started, setStarted] = useState(false);

  return (
    <Box sx={styles.root}>
      {showSkip && started && (
        <Box
          component="button"
          type="button"
          onClick={handleSkip}
          sx={styles.skipButton}
        >
          {skipLabel}
        </Box>
      )}
      <Box sx={styles.playerWrap}>
        <VideoPlayer
          src={videoUrl}
          fill
          autoPlay={autoPlay}
          onStarted={() => setStarted(true)}
          onEnded={onComplete}
        />
      </Box>
    </Box>
  );
};

export default ConsultationVideoOverlay;

const styles = {
  root: {
    position: "fixed" as const,
    inset: 0,
    zIndex: 9999,
    backgroundColor: "#000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  playerWrap: {
    width: "100vw",
    height: "100vh",
  },
  skipButton: {
    position: "absolute" as const,
    // Bottom-right placement (mimics the familiar "Skip Ad" pattern).
    bottom: "140px",
    right: "16px",
    zIndex: 10000,
    padding: "8px 16px",
    borderRadius: "8px",
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    border: `1px solid rgba(255, 255, 255, 0.25)`,
    color: "#fff",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: 1,
    cursor: "pointer",
    backdropFilter: "blur(4px)",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.22)",
    },
  },
};
